import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  SpecialityOption,
  bulkCreateTimeslots,
  createTimeslot,
  updateTimeslot,
} from '../../services/MasterAdmin/doctorClinicTimeslotService';
import { DoctorClinicBranchTimeslot } from '../../models/MasterAdmin/doctorClinic';
import {
  DAYS,
  INTERVALS,
  SessionQueueItem,
  extractTimeslotApiError,
  from12hTo24h,
  generateSlots,
  normConsultationType,
  normalizeWeekdayLabel,
  parseTimeToMinutes,
  sessionsOverlapSameSpeciality,
  to12h,
} from '../../utils/doctorClinicTimeslotUtils';

interface EditorModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  vendorId: string;
  consultationType: 'Online' | 'Offline';
  branchId: string | null;
  branchName: string;
  specialityOptions: SpecialityOption[];
  existingTimeslots: DoctorClinicBranchTimeslot[];
  editingSlot?: DoctorClinicBranchTimeslot | null;
  onClose: () => void;
  onSaved: () => void;
}

const DoctorClinicTimeslotEditorModal: React.FC<EditorModalProps> = ({
  open,
  mode,
  vendorId,
  consultationType,
  branchId,
  branchName,
  specialityOptions,
  existingTimeslots,
  editingSlot,
  onClose,
  onSaved,
}) => {
  const isEdit = mode === 'edit';

  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedSpecialities, setSelectedSpecialities] = useState<string[]>([]);
  const [startTime24, setStartTime24] = useState('');
  const [endTime24, setEndTime24] = useState('');
  const [intervalMinutes, setIntervalMinutes] = useState(30);
  const [sessionQueue, setSessionQueue] = useState<SessionQueueItem[]>([]);
  const [queueError, setQueueError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingQueueIndex, setEditingQueueIndex] = useState<number | null>(null);

  const daysInUse = useMemo(() => {
    const set = new Set<string>();
    existingTimeslots.forEach((t) => {
      if (isEdit && editingSlot && t.timeSlotID === editingSlot.timeSlotID) return;
      if (normConsultationType(t.consultationType) !== consultationType) return;
      set.add(normalizeWeekdayLabel(t.day));
    });
    return set;
  }, [existingTimeslots, consultationType, isEdit, editingSlot]);

  const availableDays = useMemo(
    () => (isEdit && editingSlot ? [normalizeWeekdayLabel(editingSlot.day)] : DAYS.filter((d) => !daysInUse.has(d))),
    [isEdit, editingSlot, daysInUse]
  );

  const resolveSpeciality = (specId: string) =>
    specialityOptions.find((o) => o.specialityId === specId) ||
    specialityOptions.find((o) => o.speciality === specId);

  useEffect(() => {
    if (!open) return;
    setQueueError('');
    setSaveError('');
    setEditingQueueIndex(null);
    setStartTime24('');
    setEndTime24('');
    setIntervalMinutes(30);
    setSelectedSpecialities([]);

    if (isEdit && editingSlot) {
      const specId =
        (editingSlot as DoctorClinicBranchTimeslot & { specialityId?: string }).specialityId || '';
      const day = normalizeWeekdayLabel(editingSlot.day);
      setSelectedDays([day]);
      setSessionQueue([
        {
          timeSlotID: editingSlot.timeSlotID,
          day,
          specialityId: specId,
          speciality: editingSlot.speciality || '',
          startTime: editingSlot.startTime,
          endTime: editingSlot.endTime,
          intervalMinutes: editingSlot.intervalMinutes || 30,
          isActive: true,
          consultationType,
        },
      ]);
    } else {
      setSelectedDays([]);
      setSessionQueue([]);
    }
  }, [open, isEdit, editingSlot, consultationType]);

  const toggleDay = (day: string) => {
    if (isEdit || sessionQueue.length > 0) return;
    setSelectedDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const toggleSpeciality = (specId: string) => {
    setSelectedSpecialities((prev) =>
      prev.includes(specId) ? prev.filter((s) => s !== specId) : [...prev, specId]
    );
  };

  const validateTimeWindow = (start24: string, end24: string, interval: number) => {
    const start = to12h(start24);
    const end = to12h(end24);
    if (!start || !end) return 'Please set start and end time.';
    const startMins = parseTimeToMinutes(start);
    let endMins = parseTimeToMinutes(end);
    if (endMins === 0 && startMins > 720) endMins = 1440;
    const diff = endMins - startMins;
    if (diff < 15) return 'End time must be at least 15 minutes after start time.';
    if (diff < interval) return 'Slot duration is too long for the selected time range.';
    return '';
  };

  const addToQueue = () => {
    setQueueError('');
    const daysForAdd = isEdit
      ? selectedDays
      : selectedDays.length > 0
        ? DAYS.filter((d) => selectedDays.includes(d))
        : [];

    if (!isEdit && daysForAdd.length === 0) {
      setQueueError('Select at least one day.');
      return;
    }
    if (selectedSpecialities.length === 0) {
      setQueueError('Select at least one speciality.');
      return;
    }

    const timeErr = validateTimeWindow(startTime24, endTime24, intervalMinutes);
    if (timeErr) {
      setQueueError(timeErr);
      return;
    }

    const start = to12h(startTime24);
    const end = to12h(endTime24);

    const newItems: SessionQueueItem[] = selectedSpecialities.map((specId) => {
      const opt = resolveSpeciality(specId);
      return {
        day: daysForAdd[0] || selectedDays[0] || '',
        specialityId: opt?.specialityId || specId,
        speciality: opt?.speciality || specId,
        startTime: start,
        endTime: end,
        intervalMinutes,
        isActive: true,
        consultationType,
      };
    });

    const nextQueue =
      editingQueueIndex !== null
        ? sessionQueue.map((item, idx) => (idx === editingQueueIndex ? newItems[0] : item))
        : isEdit
          ? newItems
          : [...sessionQueue, ...newItems];

    for (let i = 0; i < nextQueue.length; i++) {
      for (let j = i + 1; j < nextQueue.length; j++) {
        if (sessionsOverlapSameSpeciality(nextQueue[i], nextQueue[j], isEdit)) {
          setQueueError(
            `Conflict: "${nextQueue[i].speciality}" overlaps another session for the same speciality.`
          );
          return;
        }
      }
    }

    setSessionQueue(nextQueue);
    setEditingQueueIndex(null);
    setSelectedSpecialities([]);
    setStartTime24('');
    setEndTime24('');
  };

  const editQueueItem = (idx: number) => {
    const item = sessionQueue[idx];
    if (!item) return;
    setEditingQueueIndex(idx);
    setStartTime24(from12hTo24h(item.startTime));
    setEndTime24(from12hTo24h(item.endTime));
    setIntervalMinutes(item.intervalMinutes);
    setSelectedSpecialities(item.specialityId ? [item.specialityId] : []);
  };

  const removeQueueItem = (idx: number) => {
    setSessionQueue((prev) => prev.filter((_, i) => i !== idx));
    if (editingQueueIndex === idx) setEditingQueueIndex(null);
  };

  const buildFinalQueue = (): SessionQueueItem[] => {
    if (sessionQueue.length > 0) return sessionQueue;

    const daysForAdd = isEdit
      ? selectedDays
      : selectedDays.length > 0
        ? DAYS.filter((d) => selectedDays.includes(d))
        : [];

    if (selectedSpecialities.length === 0 || !startTime24 || !endTime24) return [];

    const timeErr = validateTimeWindow(startTime24, endTime24, intervalMinutes);
    if (timeErr) return [];

    const start = to12h(startTime24);
    const end = to12h(endTime24);

    return selectedSpecialities.map((specId) => {
      const opt = resolveSpeciality(specId);
      return {
        day: daysForAdd[0] || '',
        specialityId: opt?.specialityId || specId,
        speciality: opt?.speciality || specId,
        startTime: start,
        endTime: end,
        intervalMinutes,
        isActive: true,
        consultationType,
      };
    });
  };

  const handleCreate = async () => {
    const daysToCreate = isEdit
      ? selectedDays
      : selectedDays.length > 0
        ? DAYS.filter((d) => selectedDays.includes(d))
        : [];

    let finalQueue = buildFinalQueue();

    if (finalQueue.length === 0) {
      toast.error('Add at least one session to the queue, or fill the form completely.');
      return;
    }
    if (!isEdit && daysToCreate.length === 0) {
      toast.error('Select at least one day.');
      return;
    }

    const branchIdForPayload = consultationType === 'Online' ? null : branchId;
    setSaving(true);
    setSaveError('');

    try {
      if (isEdit && editingSlot) {
        const item = finalQueue[0];
        await updateTimeslot(editingSlot.timeSlotID, {
          vendorId,
          consultationType,
          clinicBranchId: branchIdForPayload,
          day: daysToCreate[0] || item.day,
          specialityId: item.specialityId,
          startTime: item.startTime,
          endTime: item.endTime,
          intervalMinutes: item.intervalMinutes,
          isActive: true,
        });
        toast.success('Timeslot updated successfully.');
      } else {
        const templates = finalQueue.map((s) => ({
          specialityId: s.specialityId,
          startTime: s.startTime,
          endTime: s.endTime,
          intervalMinutes: s.intervalMinutes,
          isActive: s.isActive,
        }));

        for (const day of daysToCreate) {
          if (templates.length === 1) {
            const s = templates[0];
            await createTimeslot({
              vendorId,
              consultationType,
              clinicBranchId: branchIdForPayload,
              day,
              specialityId: s.specialityId,
              startTime: s.startTime,
              endTime: s.endTime,
              intervalMinutes: s.intervalMinutes,
              isActive: s.isActive,
            });
          } else {
            await bulkCreateTimeslots({
              vendorId,
              consultationType,
              clinicBranchId: branchIdForPayload,
              day,
              templates,
            });
          }
        }

        const dayLabel =
          daysToCreate.length > 1 ? `${daysToCreate.length} days` : daysToCreate[0] || '';
        toast.success(
          `Timeslot${finalQueue.length > 1 ? 's' : ''} created${dayLabel ? ` for ${dayLabel}` : ''}.`
        );
      }
      onSaved();
      onClose();
    } catch (err) {
      const msg = extractTimeslotApiError(err);
      setSaveError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const canAddToQueue =
    (isEdit ? selectedDays.length > 0 : selectedDays.length > 0) &&
    selectedSpecialities.length > 0 &&
    startTime24 &&
    endTime24;

  const canCreate =
    sessionQueue.length > 0 ||
    (selectedDays.length > 0 && selectedSpecialities.length > 0 && startTime24 && endTime24);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-3 sm:p-4">
      <div
        className="flex max-h-[92vh] w-full max-w-5xl flex-col rounded-xl bg-white shadow-xl dark:bg-gray-800"
        role="dialog"
        aria-modal="true"
        aria-labelledby="timeslot-modal-title"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <h2 id="timeslot-modal-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              {isEdit ? 'Edit timeslot' : 'Create timeslot'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {consultationType === 'Online' ? 'Online consultation' : branchName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-hidden lg:grid-cols-2">
          {/* Left: form */}
          <div className="overflow-y-auto border-b border-gray-200 p-5 lg:border-b-0 lg:border-r dark:border-gray-700">
            <div className="space-y-5">
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {isEdit ? 'Day' : 'Days'}
                  </label>
                  {!isEdit && availableDays.length > 0 && sessionQueue.length === 0 && (
                    <div className="flex gap-2 text-xs">
                      <button
                        type="button"
                        className="font-medium text-blue-600 hover:underline"
                        onClick={() => setSelectedDays([...availableDays])}
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        className="text-gray-500 hover:underline"
                        onClick={() => setSelectedDays([])}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
                {isEdit ? (
                  <p className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                    {selectedDays[0] || '—'}
                  </p>
                ) : availableDays.length === 0 ? (
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    All days already scheduled. Edit or delete an existing slot first.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {availableDays.map((day) => {
                      const selected = selectedDays.includes(day);
                      const locked = sessionQueue.length > 0;
                      return (
                        <button
                          key={day}
                          type="button"
                          disabled={locked}
                          onClick={() => toggleDay(day)}
                          className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                            selected
                              ? 'border-blue-600 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-300'
                              : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-gray-500'
                          } ${locked ? 'cursor-not-allowed opacity-60' : ''}`}
                        >
                          {day.slice(0, 3)}
                        </button>
                      );
                    })}
                  </div>
                )}
                {!isEdit && selectedDays.length > 0 && (
                  <p className="mt-1.5 text-xs text-blue-600 dark:text-blue-400">
                    {selectedDays.length} day{selectedDays.length > 1 ? 's' : ''} selected
                  </p>
                )}
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    Specialities
                  </label>
                  {specialityOptions.length > 0 && (
                    <div className="flex gap-2 text-xs">
                      <button
                        type="button"
                        className="font-medium text-blue-600 hover:underline"
                        onClick={() =>
                          setSelectedSpecialities(
                            specialityOptions.map((o) => o.specialityId).filter(Boolean)
                          )
                        }
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        className="text-gray-500 hover:underline"
                        onClick={() => setSelectedSpecialities([])}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>
                {specialityOptions.length === 0 ? (
                  <p className="text-sm text-gray-500">No specialities found.</p>
                ) : (
                  <div className="flex max-h-36 flex-wrap gap-2 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-700/40">
                    {specialityOptions.map((opt) => {
                      const id = opt.specialityId || opt.speciality;
                      const selected = selectedSpecialities.includes(id);
                      const disabled = !opt.specialityId;
                      return (
                        <button
                          key={id}
                          type="button"
                          disabled={disabled}
                          onClick={() => toggleSpeciality(id)}
                          className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                            selected
                              ? 'border-blue-600 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-300'
                              : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-gray-500'
                          } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                        >
                          {opt.speciality}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                    Start
                  </label>
                  <input
                    type="time"
                    value={startTime24}
                    onChange={(e) => setStartTime24(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                    End
                  </label>
                  <input
                    type="time"
                    value={endTime24}
                    onChange={(e) => setEndTime24(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                    Duration
                  </label>
                  <select
                    value={intervalMinutes}
                    onChange={(e) => setIntervalMinutes(Number(e.target.value))}
                    className="w-full rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    {INTERVALS.map((i) => (
                      <option key={i} value={i}>
                        {i} min
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                type="button"
                onClick={addToQueue}
                disabled={!canAddToQueue}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200"
              >
                <PlusIcon className="h-4 w-4" />
                {editingQueueIndex !== null ? 'Update session' : 'Add to queue'}
                {!isEdit && selectedDays.length > 1 ? ` (${selectedDays.length} days)` : ''}
              </button>
              {queueError && (
                <p className="text-sm text-red-600 dark:text-red-400">{queueError}</p>
              )}
            </div>
          </div>

          {/* Right: queue */}
          <div className="flex min-h-0 flex-col overflow-hidden bg-gray-50/80 dark:bg-gray-900/30">
            <div className="border-b border-gray-200 px-5 py-3 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Added sessions</h3>
              {!isEdit && selectedDays.length > 0 && sessionQueue.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Applies to: {selectedDays.join(', ')}
                </p>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {sessionQueue.length === 0 ? (
                <div className="flex h-full min-h-[12rem] flex-col items-center justify-center text-center text-sm text-gray-500 dark:text-gray-400">
                  <p>No sessions in queue yet.</p>
                  <p className="mt-1 text-xs">Select days & specialities, set times, then Add to queue.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sessionQueue.map((item, idx) => (
                    <div
                      key={`${item.specialityId}-${idx}`}
                      className={`rounded-lg border bg-white p-3 dark:bg-gray-800 ${
                        editingQueueIndex === idx
                          ? 'border-blue-500 ring-1 ring-blue-500'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <span className="rounded-md bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                          {item.speciality}
                        </span>
                        <div className="flex shrink-0 gap-2 text-xs">
                          <button
                            type="button"
                            className="font-medium text-blue-600 hover:underline"
                            onClick={() => editQueueItem(idx)}
                          >
                            Edit
                          </button>
                          {!isEdit && (
                            <button
                              type="button"
                              className="font-medium text-red-600 hover:underline"
                              onClick={() => removeQueueItem(idx)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {to12h(item.startTime)} – {to12h(item.endTime)} · {item.intervalMinutes} min
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {generateSlots(item.startTime, item.endTime, item.intervalMinutes)
                          .slice(0, 8)
                          .map((slot) => (
                            <span
                              key={slot}
                              className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300"
                            >
                              {slot}
                            </span>
                          ))}
                        {generateSlots(item.startTime, item.endTime, item.intervalMinutes).length > 8 && (
                          <span className="text-[10px] text-gray-400">+more</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="shrink-0 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
          {saveError && (
            <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
              {saveError}
            </p>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={saving || (!isEdit && availableDays.length === 0) || !canCreate}
              className="flex-[2] rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
            >
              {saving
                ? 'Saving...'
                : isEdit
                  ? 'Save changes'
                  : `Create schedule${selectedDays.length > 1 ? ` (${selectedDays.length} days)` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorClinicTimeslotEditorModal;
